'use client';

import { useState, useEffect } from 'react';
import './CalendarComponent.css';

const CalendarComponent = ({ onDateSelect, selectedDate, minDate, maxDate, isOpen, onClose }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [showYearDropdown, setShowYearDropdown] = useState(false);

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const handleDateClick = day => {
    const newDate = new Date(currentYear, currentMonth, day);
    if (minDate && newDate < minDate) return;
    if (maxDate && newDate > maxDate) return;
    onDateSelect(newDate);
    onClose && onClose();
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleYearChange = year => {
    setCurrentYear(year);
    setShowYearDropdown(false);
  };

  const isDateDisabled = day => {
    const date = new Date(currentYear, currentMonth, day);
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };

  const isDateSelected = day => {
    if (!selectedDate) return false;
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === currentMonth &&
      selectedDate.getFullYear() === currentYear
    );
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="dob-calendar-day-empty"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const disabled = isDateDisabled(day);
      const selected = isDateSelected(day);
      days.push(
        <button
          key={day}
          className={`dob-calendar-day ${selected ? 'dob-calendar-day-selected' : ''} ${disabled ? 'dob-calendar-day-disabled' : ''}`}
          onClick={() => !disabled && handleDateClick(day)}
          disabled={disabled}
          type="button"
        >
          {day}
        </button>
      );
    }

    return days;
  };

  useEffect(() => {
    const handleClickOutside = event => {
      // Close when clicking outside the dropdown content
      if (isOpen) {
        const container = document.querySelector('.dob-calendar-dropdown-container');
        if (container && !container.contains(event.target)) {
          onClose && onClose();
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="dob-calendar-dropdown-container">
      <div className="dob-calendar-modal">
        <div className="dob-calendar-header">
          <button type="button" className="dob-calendar-nav-button" onClick={handlePrevMonth}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="15,18 9,12 15,6"></polyline>
            </svg>
          </button>

          <div className="dob-calendar-month-year">
            <span className="dob-calendar-month">{months[currentMonth]}</span>
            <div className="dob-calendar-year-dropdown-container">
              <button
                type="button"
                className="dob-calendar-year-button"
                onClick={() => setShowYearDropdown(!showYearDropdown)}
              >
                {currentYear}
                <svg
                  className={`dob-calendar-year-arrow ${showYearDropdown ? 'dob-calendar-year-arrow-up' : ''}`}
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="6,9 12,15 18,9"></polyline>
                </svg>
              </button>
              {showYearDropdown && (
                <div className="dob-calendar-year-dropdown">
                  {Array.from(
                    { length: new Date().getFullYear() - 1900 + 1 },
                    (_, i) => new Date().getFullYear() - i
                  ).map(year => (
                    <button
                      key={year}
                      type="button"
                      className={`dob-calendar-year-option ${year === currentYear ? 'dob-calendar-year-option-selected' : ''}`}
                      onClick={() => handleYearChange(year)}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button type="button" className="dob-calendar-nav-button" onClick={handleNextMonth}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="9,18 15,12 9,6"></polyline>
            </svg>
          </button>
        </div>

        <div className="dob-calendar-weekdays">
          {daysOfWeek.map(day => (
            <div key={day} className="dob-calendar-weekday">
              {day.toUpperCase()}
            </div>
          ))}
        </div>

        <div className="dob-calendar-days">{renderCalendarDays()}</div>
      </div>
    </div>
  );
};

export default CalendarComponent;
